/**
 * End natural spawning: endermen (vanilla: the only End mob) in groups of 1-4 on
 * end stone around the player, on the main island and the outer islands.
 * Runs instead of the overworld Spawner while the player is in the End
 * (main.ts disables EntityManager.spawningEnabled there), mirroring NetherSpawner.
 */

import { BLOCKS, IS_SOLID } from '../../blocks';
import type { MobType } from '../../game/types';
import type { World } from '../../world/chunk';
import { ChunkState } from '../../world/chunk';

/** Monster cap near the player. */
export const END_CAP = 14;

export interface EndSpawnHost {
  world: World;
  rnd(): number;
  player(): { x: number; y: number; z: number };
  spawn(type: MobType, x: number, y: number, z: number): unknown;
  /** Living enderman count. */
  count(): number;
}

let endStoneId = -2;
const isEndStone = (id: number): boolean => {
  if (endStoneId === -2) endStoneId = BLOCKS.findIndex((b) => b?.name === 'End Stone');
  return endStoneId >= 0 && id === endStoneId;
};

export class EndSpawner {
  private timer = 0;
  constructor(private readonly host: EndSpawnHost) {}

  tick(): void {
    if (++this.timer < 20) return;
    this.timer = 0;
    const h = this.host;
    let have = h.count();
    if (have >= END_CAP) return;
    const p = h.player();
    const ang = h.rnd() * Math.PI * 2;
    const dist = 24 + h.rnd() * 40;
    const x = Math.floor(p.x + Math.cos(ang) * dist);
    const z = Math.floor(p.z + Math.sin(ang) * dist);
    const y0 = this.floor(x, Math.floor(p.y), z);
    if (y0 < 0) return;
    const n = 1 + Math.floor(h.rnd() * 4);
    for (let i = 0; i < n && have < END_CAP; i++) {
      const sx = i === 0 ? x : x + Math.floor(h.rnd() * 7) - 3;
      const sz = i === 0 ? z : z + Math.floor(h.rnd() * 7) - 3;
      const sy = i === 0 ? y0 : this.floor(sx, y0 + 2, sz);
      if (sy < 0 || Math.abs(sy - y0) > 3) continue;
      const dx = sx - p.x, dz = sz - p.z;
      if (dx * dx + dz * dz < 24 * 24) continue;
      h.spawn('enderman', sx + 0.5, sy, sz + 0.5);
      have++;
    }
  }

  /** Feet y of a free 3-tall spot on end stone near y (scanning down), or -1. */
  private floor(x: number, y: number, z: number): number {
    const w = this.host.world;
    const c = w.getChunk(x >> 4, z >> 4);
    if (!c || c.state !== ChunkState.Ready) return -1;
    for (let yy = Math.min(y + 16, 120); yy >= Math.max(y - 24, 2); yy--) {
      if (!isEndStone(w.getBlock(x, yy - 1, z))) continue;
      if (IS_SOLID[w.getBlock(x, yy, z)] || IS_SOLID[w.getBlock(x, yy + 1, z)] || IS_SOLID[w.getBlock(x, yy + 2, z)]) continue;
      return yy;
    }
    return -1;
  }
}
